import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../utils/api';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import { isDevAuthEnabled, devSignIn } from '../utils/devAuth';
// NinjaLayer now rendered globally in App.js

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Return only to an explicitly requested same-origin Builder route.
  // Preserve the normal /home post-login destination for other workflows.
  const fromPath = location.state?.from?.pathname || '';
  const queryNext = new URLSearchParams(location.search).get('next');
  // Strict internal allowlist: never allow arbitrary external return URLs.
  const postLoginPath = fromPath === '/team/builder' || fromPath.startsWith('/team/builder/')
    ? fromPath
    : queryNext === '/team/builder' ? '/team/builder'
    : fromPath.startsWith('/builder/') ? fromPath
    : '/home';

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      localStorage.setItem('token', token);
      toast.success('Logged in with Google!');
      navigate(postLoginPath);
    }
  }, [navigate, postLoginPath]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/login', { email, password });
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success('Welcome back!');
      navigate(postLoginPath);
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Login failed';
      if (isDevAuthEnabled()) {
        const { user } = devSignIn({ email, name: email.split('@')[0] });
        toast.success(`Dev sign-in: ${user.name}`);
        navigate(postLoginPath);
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const response = await api.get('/auth/google');
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      } else {
        toast.error(response.data.message || 'Google OAuth not configured.');
      }
    } catch (err) {
      if (isDevAuthEnabled()) {
        const { user } = devSignIn({ email: 'dev-google@example.com', name: 'Dev Google' });
        toast.success(`Dev Google sign-in: ${user.name}`);
        navigate(postLoginPath);
      } else {
        toast.error('Failed to initiate Google OAuth. Network Error');
      }
    }
  };

  return (
    <>
      {/* AuthShell provides NinjaLayer masthead + centered glass card */}

      <Card className="border border-emerald-500/30 bg-black/70 backdrop-blur-2xl shadow-2xl shadow-emerald-500/20">
        <CardContent className="pt-6">
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-emerald-300">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-emerald-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-black/50 border-emerald-500/30 text-white placeholder:text-slate-500 focus:border-emerald-400 focus:ring-emerald-400/20"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-emerald-300">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-emerald-500" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 bg-black/50 border-emerald-500/30 text-white placeholder:text-slate-500 focus:border-emerald-400 focus:ring-emerald-400/20"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-emerald-500 hover:text-emerald-300"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="text-right">
                <Link to="/forgot-password" className="text-xs text-emerald-400/70 hover:text-emerald-300">
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button type="submit" className="w-full bg-gradient-to-r from-emerald-600 to-orange-500 hover:from-emerald-500 hover:to-orange-400 text-white font-semibold shadow-lg shadow-emerald-500/25" disabled={loading} data-testid="login-button">
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-emerald-500/20"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-black/60 px-2 text-emerald-400/60">Or continue with</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 hover:border-emerald-400"
            onClick={handleGoogleLogin}
            data-testid="google-login-button"
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Sign in with Google
          </Button>

          <p className="mt-6 text-center text-sm text-emerald-400/60">
            Don&apos;t have an account?{' '}
            <Link to="/register" className="font-medium text-orange-400 hover:text-orange-300 hover:underline">
              Sign up
            </Link>
          </p>
        </CardContent>
      </Card>
    </>
  );
};

export default Login;